import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Instagram, Twitter, Linkedin } from "lucide-react";
import Beams from "./Beams";
import "./FooterSection.css";

import Colombo from "../assets/colombo.jpg";
import CV from "../assets/DulainGunawardhanaCV.pdf";

// Social links
const socialLinks = [
    {
        name: "Email",
        url: "mailto:03dulain@gmail.com",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-white">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        name: "Instagram",
        url: "https://instagram.com/notdulain",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
                <defs>
                    <linearGradient id="ig-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f09433" />
                        <stop offset="25%" stopColor="#e6683c" />
                        <stop offset="50%" stopColor="#dc2743" />
                        <stop offset="75%" stopColor="#cc2366" />
                        <stop offset="100%" stopColor="#bc1888" />
                    </linearGradient>
                </defs>
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="url(#ig-gradient)" strokeWidth="2" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="url(#ig-gradient)" strokeWidth="2" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="url(#ig-gradient)" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        name: "GitHub",
        url: "https://github.com/notdulain",
        icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-white">
                <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0 0 22 12.017C22 6.484 17.522 2 12 2z" />
            </svg>
        ),
    },
    {
        name: "LinkedIn",
        url: "https://www.linkedin.com/in/dulain-nethwin-gunawardhana-41b251294/",
        icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-[#0077b5]">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
        ),
    },
];

export default function FooterSection() {
    // Click counter state
    const [clickCount, setClickCount] = useState(0);

    // Location/time state
    const [currentTime, setCurrentTime] = useState(new Date());

    // Contact form state
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");

    // Photo state
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const photos = Array.from({ length: 36 }, (_, i) => `https://res.cloudinary.com/dhntips7e/image/upload/v1770534012/${i + 1}.jpg`);
    const [result, setResult] = useState("");

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setResult("Sending...");
        const formData = new FormData(event.currentTarget);


        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: formData
            });

            const data = await response.json();
            if (data.success) {
                setResult("Success! Message sent.");
                // Optional: clear form
                setName("");
                setMessage("");
            } else {
                setResult("Error! " + data.message);
            }
        } catch (error) {
            setResult("Something went wrong!");
        }
    };

    // Load click count from localStorage
    useEffect(() => {
        const saved = localStorage.getItem("dulain_click_count");
        if (saved) setClickCount(parseInt(saved, 10));
    }, []);

    // Update time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Auto-cycle photos
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
        }, 2000); // Change photo every 2 seconds
        return () => clearInterval(timer);
    }, [photos.length]);

    // Handle click counter
    const handleClick = () => {
        const newCount = clickCount + 1;
        setClickCount(newCount);
        localStorage.setItem("dulain_click_count", newCount.toString());
    };

    // Format time for Sri Lanka timezone
    const colomboTime = new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Colombo",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(currentTime);

    return (
        <section className="footer-section relative pt-24 md:pt-32 pb-6 md:pb-8 px-4 md:px-6 bg-[#111118] overflow-hidden text-white">
            {/* Beams Background */}
            <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                <Beams
                    beamWidth={3}
                    beamHeight={30}
                    beamNumber={20}
                    lightColor="#ffffff"
                    speed={2}
                    noiseIntensity={1.75}
                    scale={0.2}
                    rotation={30}
                />
            </div>

            {/* Top fade overlay - smooth transition from terminal section */}
            <div
                className="absolute top-0 inset-x-0 h-48 z-[1] pointer-events-none"
                style={{
                    background: "linear-gradient(to bottom, #0c0c0c 0%, transparent 100%)",
                }}
            />

            <div className="max-w-7xl mx-auto relative z-10 pb-0">
                <motion.div
                    className="space-y-8 md:space-y-12"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: false, amount: 0.2 }}
                    variants={{
                        hidden: { opacity: 0 },
                        show: { opacity: 1, transition: { staggerChildren: 0.16 } },
                    }}
                >
                    {/* Header Section - Refined */}
                    <motion.div
                        className="space-y-2 text-center md:text-left"
                        variants={{
                            hidden: { opacity: 0, y: 20 },
                            show: { opacity: 1, y: 0, transition: { duration: 0.8 } }
                        }}
                    >
                        <div className="inline-block relative">
                            <span className="text-sm text-white/40 uppercase tracking-widest pl-1 font-sans">04 — Let's Talk</span>
                        </div>

                        <div className="flex flex-col gap-0">
                            <h2 className="text-5xl md:text-[5vw] font-display text-white">
                                Say Hi! <span className="block md:inline text-2xl md:text-[3vw] text-white italic"> or don't 🥀 I'll still overthink it</span>
                            </h2>
                        </div>


                    </motion.div>

                    {/* The Grid Architect Layout - 3 Columns */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-px">

                        {/* LEFT: Contact Form (Span 5) */}
                        <motion.div
                            className="md:col-span-5 bg-[#0c0c0c] relative group"
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                show: { opacity: 1, y: 0, transition: { duration: 0.8 } }
                            }}
                        >
                            <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <form onSubmit={onSubmit} className="relative z-10 p-8 md:p-10 flex flex-col gap-6 h-full justify-center">
                                <div className="space-y-1">
                                    <h3 className="text-4xl font-display text-white">Send a Message</h3>
                                    <p className="text-white/40 text-md font-sans">est. response time: 2-3 business minutes</p>
                                </div>

                                <input type="hidden" name="access_key" value="4a883b3d-9787-4dcc-914a-b70ddedee07d" />

                                <div className="space-y-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-sans text-white/40 uppercase tracking-wider">Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                            className="w-full bg-transparent border-b border-white/20 py-2 text-white placeholder-white/20 focus:outline-none focus:border-white/60 transition-colors font-sans text-md"
                                            placeholder="Clawd Clawderberg"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-sans text-white/40 uppercase tracking-wider">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            className="w-full bg-transparent border-b border-white/20 py-2 text-white placeholder-white/20 focus:outline-none focus:border-white/60 transition-colors font-sans text-mdl"
                                            placeholder="no temp mails plis 🙏🏻"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-sans text-white/40 uppercase tracking-wider">Message</label>
                                        <textarea
                                            name="message"
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            required
                                            className="w-full h-24 bg-transparent border-b border-white/20 py-2 text-white placeholder-white/20 focus:outline-none focus:border-white/60 transition-colors font-sans text-md resize-none"
                                            placeholder="Tell me about your project"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-between items-center pt-2">
                                    <button
                                        type="submit"
                                        className="px-6 py-3 bg-white text-black font-sans text-xs font-bold uppercase tracking-widest hover:bg-white/90 transition-colors"
                                    >
                                        {result === "Sending..." ? "Sending..." : "SEND MESSAGE"}
                                    </button>
                                    {result && (
                                        <p className={`text-xs font-sans ${result.includes("Success") ? "text-green-400" : "text-red-400"}`}>
                                            {result}
                                        </p>
                                    )}
                                </div>
                            </form>
                        </motion.div>

                        {/* MIDDLE: Photos Card (Span 4) */}
                        <motion.div
                            className="md:col-span-4 bg-[#0c0c0c] relative group overflow-hidden"
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                show: { opacity: 1, y: 0, transition: { duration: 0.8 } }
                            }}
                        >
                            <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />
                            <div className="relative h-full w-full min-h-[400px]">
                                <AnimatePresence>
                                    <motion.img
                                        key={currentPhotoIndex}
                                        src={photos[currentPhotoIndex]}
                                        alt="Moments"
                                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                                        initial={{ opacity: 0, scale: 1.1 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 1.2 }}
                                    />
                                </AnimatePresence>
                            </div>
                        </motion.div>

                        {/* RIGHT: Utility Stack (Span 3) */}
                        <div className="md:col-span-3 flex flex-col gap-px bg-white/5">
                            {/* Location Card */}
                            <motion.div
                                className="flex-1 bg-[#0c0c0c] p-8 relative group"
                                variants={{
                                    hidden: { opacity: 0, x: 20 },
                                    show: { opacity: 1, x: 0, transition: { duration: 0.8 } }
                                }}
                            >
                                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="relative z-10 h-full flex flex-col justify-between gap-6">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-sans font-bold text-white/40 uppercase tracking-wider flex items-center gap-2">
                                            Currently Based In <span className="text-red-500">📍</span>
                                        </span>
                                    </div>

                                    <div className="relative w-full aspect-[2/1] overflow-hidden">
                                        <img
                                            src={Colombo}
                                            alt="Colombo Map"
                                            className="absolute inset-0 w-full h-full object-cover opacity-100 group-hover:opacity-100 transition-opacity duration-500 grayscale group-hover:grayscale-[0.5]"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-transparent to-transparent opacity-50" />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <h3 className="text-[14px] font-sans uppercase tracking-[0.2em] text-white/50">Colombo, LK</h3>
                                        <div className="flex items-center gap-2 text-white/60 font-sans text-xs">
                                            {parseInt(new Intl.DateTimeFormat("en-US", {
                                                timeZone: "Asia/Colombo",
                                                hour: "numeric",
                                                hour12: false,
                                            }).format(currentTime)) >= 6 && parseInt(new Intl.DateTimeFormat("en-US", {
                                                timeZone: "Asia/Colombo",
                                                hour: "numeric",
                                                hour12: false,
                                            }).format(currentTime)) < 18 ? "☀️" : "🌙"} {colomboTime}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Socials Card */}
                            <motion.div
                                className="flex-1 bg-[#0c0c0c] p-8 relative group"
                                variants={{
                                    hidden: { opacity: 0, x: 20 },
                                    show: { opacity: 1, x: 0, transition: { duration: 0.8 } }
                                }}
                            >
                                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="relative z-10 h-full flex flex-col justify-between gap-2">
                                    <span className="text-xs font-sans text-white/40 uppercase tracking-wider">Connect</span>
                                    <div className="grid grid-cols-2 gap-4">
                                        {socialLinks.map((link) => (
                                            <a
                                                key={link.name}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 text-white/60 hover:text-white transition-colors group/link"
                                            >
                                                {/* Clean minimalist icon */}
                                                <div className="flex items-center justify-center transition-transform group-hover/link:scale-110">
                                                    {link.icon}
                                                </div>
                                                <span className="font-sans text-[10px] font-bold uppercase tracking-wider block">{link.name}</span>
                                            </a>
                                        ))}
                                    </div>
                                    <div className="pt-2 mt-1">
                                        <a
                                            //href="/DulainGunawardhanaCV.pdf"
                                            href={CV}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center justify-between px-4 py-3 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all group/cv w-full"
                                        >
                                            <span className="text-white/60 font-sans text-xs font-bold uppercase tracking-wider group-hover/cv:text-white transition-colors">Download CV</span>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white/60 group-hover/cv:text-white transition-colors">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="7 10 12 15 17 10" />
                                                <line x1="12" y1="15" x2="12" y2="3" />
                                            </svg>
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    <motion.div
                        className="md:pl-2 mt-1 mb-1"
                        variants={{
                            hidden: { opacity: 0, y: 20 },
                            show: { opacity: 1, y: 0, transition: { duration: 0.8 } }
                        }}
                    >
                        <p className="font-display font-light text-sm text-white/80 md:text-xl max-w-full">
                            whether you want to discuss the meaning of existence or just debug code at 2am 👆🏻
                        </p>
                    </motion.div>

                    {/* Bottom Utility Bar */}
                    <motion.div
                        className="py-4 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-sans text-white/40"
                        variants={{
                            hidden: { opacity: 0 },
                            show: { opacity: 1, transition: { duration: 0.8 } }
                        }}
                    >
                        <span>© 2026 Dulain Gunawardhana</span>
                        <span className="text-white/60 flex items-center gap-2 font-sans">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                            All systems probably online
                        </span>
                    </motion.div>

                </motion.div>
            </div>
        </section>
    );
}
